import "./Map.scss";
import { FormControl, Modal, TextField, Select, MenuItem } from "@mui/material";
import CancelIcon from "@mui/icons-material/CloseOutlined";
import LocationIcon from "@mui/icons-material/LocationOn";
import GreenButton from "../GreenButton/GreenButton";
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../utils/api";
// Import Leaflet components
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for Vite production build - add ?url suffix
import icon from "leaflet/dist/images/marker-icon.png?url";
import iconShadow from "leaflet/dist/images/marker-shadow.png?url";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png?url";

import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { SaudiArabiaAreas } from "../../SourceData"; // Import your areas array

// Fix Leaflet icon issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: iconShadow,
});

// Map Events component for click handling
function MapEvents({ onMapClick }) {
  useMapEvents({
    click(e) {
      if (onMapClick) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

// Marker component for the map
function MarkerComponent({ position, dragEnd, draggable = true }) {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (position && map) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker && dragEnd) {
        const pos = marker.getLatLng();
        dragEnd(pos.lat, pos.lng);
      }
    },
  };

  return position ? (
    <Marker
      draggable={draggable}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  ) : null;
}

const Map = (props) => {
  const { dir, t } = useLanguage();
  const language = dir === "rtl" ? "ar" : "en";
  const { updateUserData } = useAuth();
  const isMobile = window.innerWidth <= 768;
  const [loading, setLoading] = useState(false);
  const [manualStreetEdit, setManualStreetEdit] = useState(false);

  // Map state
  const [position, setPosition] = useState(null);
  const mapRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);

  // Check if we're in edit mode
  const isEditMode = !!props.editAddress;

  console.log("Edit Address Data:", props.editAddress);

  // Form state
  const [formData, setFormData] = useState({
    addressName: "",
    area: "", // Area ID (e.g., "SA-01")
    governorate: "", // Governorate ID (e.g., "SA-01-01")
    street: "",
    buildingNumber: "",
    floorNumber: "",
    addressSign: "",
    coordinates: null,
    locationConfirmed: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const [availableGovernorates, setAvailableGovernorates] = useState([]);

  // Initialize form data if in edit mode
  useEffect(() => {
    if (props.editAddress) {
      // Extract coordinates from the array [lng, lat] format to an object
      let coordinates = null;
      if (
        props.editAddress.coordinates &&
        props.editAddress.coordinates.length === 2
      ) {
        coordinates = {
          lng: props.editAddress.coordinates[0],
          lat: props.editAddress.coordinates[1],
        };

        // Update the map position
        setPosition([coordinates.lat, coordinates.lng]);
      }

      // Find matching area and governorate from edit data
      let matchedAreaId = "";
      let matchedGovernorateId = "";

      const areaName = props.editAddress.area; // This is the area NAME from backend
      const governorateName = props.editAddress.governorate; // This is the governorate NAME

      console.log("Trying to match:", { areaName, governorateName });

      // Search through areas to find matches
      for (const area of SaudiArabiaAreas) {
        // First, check if this is the correct area by name
        const isMatchingArea =
          area.areaNameArabic === areaName ||
          area.areaNameEnglish === areaName;

        if (isMatchingArea) {
          matchedAreaId = area.areaId;

          // Now find the governorate within this area
          for (const gov of area.governorates) {
            if (
              gov.nameArabic === governorateName ||
              gov.nameEnglish === governorateName
            ) {
              matchedGovernorateId = gov.id;
              break;
            }
          }
          break; // Exit area loop once we found the matching area
        }
      }

      // If we didn't find area by name, maybe backend sends IDs directly
      // Let's check if the area value is actually an ID
      if (!matchedAreaId && areaName) {
        const areaById = SaudiArabiaAreas.find(a => a.areaId === areaName);
        if (areaById) {
          matchedAreaId = areaName; // It's already an ID
          
          // Check if governorate is also an ID
          if (governorateName) {
            const govById = areaById.governorates.find(g => g.id === governorateName);
            if (govById) {
              matchedGovernorateId = governorateName; // It's already an ID
            }
          }
        }
      }

      console.log("Matched IDs:", { matchedAreaId, matchedGovernorateId });

      // Set form data from the address to edit
      setFormData({
        addressName: props.editAddress.name || "",
        area: matchedAreaId,
        governorate: matchedGovernorateId,
        street: props.editAddress.street || "",
        buildingNumber: props.editAddress.buildingNo || "",
        floorNumber: props.editAddress.floorNo || "",
        addressSign: props.editAddress.addressSign || "",
        coordinates: coordinates,
        locationConfirmed: !!coordinates,
      });

      // Update available governorates if area is matched
      if (matchedAreaId) {
        const selectedArea = SaudiArabiaAreas.find(
          (a) => a.areaId === matchedAreaId
        );
        if (selectedArea) {
          setAvailableGovernorates(selectedArea.governorates);
        }
      }
    } else {
      // Reset form if not in edit mode
      setFormData({
        addressName: "",
        area: "",
        governorate: "",
        street: "",
        buildingNumber: "",
        floorNumber: "",
        addressSign: "",
        coordinates: null,
        locationConfirmed: false,
      });
      setAvailableGovernorates([]);
      setPosition(null);
    }
  }, [props.editAddress]);

  // Initialize map with default Riyadh position
  useEffect(() => {
    if (!props.editAddress && !position) {
      // Set default position to Riyadh (center of Saudi Arabia)
      const defaultPosition = [24.7136, 46.6753];
      setPosition(defaultPosition);
    }
  }, [props.editAddress, position]);

  // Handle area selection
  const handleAreaChange = (e) => {
    const selectedAreaId = e.target.value;
    const selectedArea = SaudiArabiaAreas.find(
      (area) => area.areaId === selectedAreaId
    );

    if (selectedArea) {
      // Update available governorates
      setAvailableGovernorates(selectedArea.governorates);

      // Set map to area coordinates
      const areaCoordinates = [
        selectedArea.coordinates[0],
        selectedArea.coordinates[1],
      ];
      setPosition(areaCoordinates);

      // Update form data
      setFormData((prev) => ({
        ...prev,
        area: selectedAreaId,
        governorate: "", // Reset governorate when area changes
        coordinates: {
          lat: selectedArea.coordinates[0],
          lng: selectedArea.coordinates[1],
        },
        locationConfirmed: false, // User still needs to confirm precise location
      }));

      // Clear area error
      if (formErrors.area) {
        setFormErrors((prev) => ({ ...prev, area: "" }));
      }
    }
  };

  // Handle governorate selection
  const handleGovernorateChange = (e) => {
    const selectedGovernorateId = e.target.value;
    const selectedGovernorate = availableGovernorates.find(
      (gov) => gov.id === selectedGovernorateId
    );

    if (selectedGovernorate) {
      // Set map to governorate coordinates
      const govCoordinates = [
        selectedGovernorate.coordinates[0],
        selectedGovernorate.coordinates[1],
      ];
      setPosition(govCoordinates);

      // Update form data
      setFormData((prev) => ({
        ...prev,
        governorate: selectedGovernorateId,
        coordinates: {
          lat: selectedGovernorate.coordinates[0],
          lng: selectedGovernorate.coordinates[1],
        },
        locationConfirmed: false, // User still needs to confirm precise location on map
      }));

      // Clear governorate error
      if (formErrors.governorate) {
        setFormErrors((prev) => ({ ...prev, governorate: "" }));
      }
    }
  };

  // Reverse geocode: coordinates to address
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=${language}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      return { display_name: "", address: {} };
    }
  };

  // Update form from map data
  const updateFormFromMap = (addressData) => {
    if (!addressData) return;

    const address = addressData.address || {};

    const updatedFormData = {
      ...formData,
      // Only update street from map if user hasn't manually edited it
      street: manualStreetEdit
        ? formData.street
        : address.road || address.pedestrian || formData.street || "",
      coordinates: {
        lat: addressData.lat || addressData.latitude,
        lng: addressData.lon || addressData.longitude,
      },
      locationConfirmed: true,
    };

    setFormData(updatedFormData);

    if (formErrors.coordinates) {
      setFormErrors((prev) => ({
        ...prev,
        coordinates: "",
      }));
    }
  };

  // Handler for map clicks
  const handleMapClick = (lat, lng) => {
    setPosition([lat, lng]);
    reverseGeocode(lat, lng).then((addressData) => {
      addressData.lat = lat;
      addressData.lon = lng;
      updateFormFromMap(addressData);
    });
  };

  // Handler for marker drags
  const handleMarkerDragEnd = (lat, lng) => {
    setPosition([lat, lng]);
    reverseGeocode(lat, lng).then((addressData) => {
      addressData.lat = lat;
      addressData.lon = lng;
      updateFormFromMap(addressData);
    });
  };

  // Current location button handler
  const handleCurrentLocationClick = () => {
    const now = Date.now();
    if (now - lastUpdateTimeRef.current < 1000) return;
    lastUpdateTimeRef.current = now;

    if (!("geolocation" in navigator)) {
      toast.error(
        t("geolocationNotSupported") || "المتصفح لا يدعم تحديد الموقع"
      );
      return;
    }

    // Show loading toast while getting location
    const loadingToast = toast.loading(
      t("gettingLocation") || "جاري تحديد موقعك..."
    );

    navigator.geolocation.getCurrentPosition(
      // Success callback
      (pos) => {
        toast.dismiss(loadingToast);
        const currentPosition = [pos.coords.latitude, pos.coords.longitude];
        setPosition(currentPosition);
        reverseGeocode(currentPosition[0], currentPosition[1]).then(
          (addressData) => {
            addressData.lat = currentPosition[0];
            addressData.lon = currentPosition[1];
            updateFormFromMap(addressData);
            toast.success(t("locationDetected") || "تم تحديد موقعك بنجاح");
          }
        );
      },
      // Error callback
      (error) => {
        toast.dismiss(loadingToast);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error(
              dir === "rtl"
                ? "تم رفض الإذن بالوصول إلى الموقع. يرجى السماح بالوصول إلى الموقع من إعدادات المتصفح"
                : "Location access denied. Please allow location access in your browser settings",
              { autoClose: 5000 }
            );

            // Show instructions based on browser
            setTimeout(() => {
              const isChrome = /Chrome/.test(navigator.userAgent);
              const isSafari =
                /Safari/.test(navigator.userAgent) &&
                !/Chrome/.test(navigator.userAgent);
              const isFirefox = /Firefox/.test(navigator.userAgent);

              let instructions = "";
              if (isChrome) {
                instructions =
                  dir === "rtl"
                    ? "انقر على أيقونة القفل في شريط العنوان ← اختر 'إعدادات الموقع' ← اسمح بالموقع"
                    : "Click the lock icon in the address bar → Choose 'Site settings' → Allow Location";
              } else if (isSafari) {
                instructions =
                  dir === "rtl"
                    ? "Safari ← التفضيلات ← مواقع الويب ← خدمات الموقع"
                    : "Safari → Preferences → Websites → Location Services";
              } else if (isFirefox) {
                instructions =
                  dir === "rtl"
                    ? "انقر على أيقونة القفل ← امسح الأذونات وأعد تحميل الصفحة"
                    : "Click the lock icon → Clear permissions and reload";
              }

              if (instructions) {
                toast.info(instructions, { autoClose: 7000 });
              }
            }, 1000);
            break;

          case error.POSITION_UNAVAILABLE:
            toast.error(
              t("locationUnavailable") || "الموقع غير متاح حالياً"
            );
            break;

          case error.TIMEOUT:
            toast.error(
              t("locationTimeout") ||
                "انتهت مهلة تحديد الموقع. يرجى المحاولة مرة أخرى"
            );
            break;

          default:
            toast.error(t("locationError") || "حدث خطأ أثناء تحديد الموقع");
        }
      },
      // Options
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Track if user manually edited the street field
    if (name === "street") {
      setManualStreetEdit(true);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.addressName)
      errors.addressName =
        t("pleaseEnterAddressName") || "يرجى إدخال اسم العنوان";
    if (!formData.area)
      errors.area = t("pleaseSelectArea") || "يرجى اختيار المنطقة";
    if (!formData.governorate)
      errors.governorate =
        t("pleaseSelectGovernorate") || "يرجى اختيار المحافظة";
    if (!formData.street)
      errors.street = t("pleaseEnterStreetName") || "يرجى إدخال اسم الشارع";
    if (!formData.coordinates || !formData.locationConfirmed) {
      errors.coordinates =
        t("pleaseConfirmLocationOnMap") ||
        "يرجى تأكيد الموقع على الخريطة بالنقر أو استخدام زر الموقع الحالي";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.locationConfirmed) {
      toast.warning(t("pleaseConfirmLocationOnMap"));
      setFormErrors({
        ...formErrors,
        coordinates:
          t("pleaseConfirmLocationOnMap") ||
          "يرجى تأكيد الموقع على الخريطة بالنقر أو استخدام زر الموقع الحالي",
      });
      if (mapRef.current) {
        mapRef.current.getContainer().scrollIntoView({ behavior: "smooth" });
      }
      return;
    }

    setLoading(true);

    if (validateForm()) {
      try {
        // Format the address data - send IDs instead of names
        const addressData = {
          name: formData.addressName,
          governorate: formData.governorate, // Send governorate ID (e.g., "SA-02-01")
          area: formData.area, // Send area ID (e.g., "SA-02")
          street: formData.street,
          buildingNo: formData.buildingNumber,
          floorNo: formData.floorNumber,
          coordinates: formData.coordinates
            ? [formData.coordinates.lng, formData.coordinates.lat]
            : null,
          addressSign: formData.addressSign || "",
        };

        let response;

        if (props.packageData && props.packageData._id) {
          response = await api.post(
            `/subscriptions/${props.packageData._id}/changeAddress`,
            { address: addressData }
          );
          toast.success(t("subscriptionAddressUpdated"));
        } else if (isEditMode) {
          response = await api.patch(
            `/users/updateAddress/${props.editAddress._id}`,
            addressData
          );
          toast.success(t("addressUpdated") || "تم تحديث العنوان بنجاح");
        } else {
          response = await api.patch("/users/addAddress", addressData);
          toast.success(t("addressAdded") || "تم إضافة العنوان بنجاح");
        }

        if (!props.packageData) {
          updateUserData(response.data.data);
        }

        props?.onSubmit?.(response.data);
        props?.onClose?.();
      } catch (error) {
        console.error(
          `Error ${
            props.packageData
              ? "updating subscription address"
              : isEditMode
              ? "updating"
              : "adding"
          } address:`,
          error
        );
        toast.error(
          error.response?.data?.message ||
            (props.packageData
              ? "حدث خطأ أثناء تحديث عنوان الاشتراك"
              : isEditMode
              ? "حدث خطأ أثناء تحديث العنوان"
              : "حدث خطأ أثناء إضافة العنوان")
        );
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={props?.open}
      onClose={props?.onClose}
      aria-labelledby="address-modal-title"
      aria-describedby="address-modal-description"
      className="map"
      sx={{
        paddingTop: isMobile ? "300px" : "0",
      }}
    >
      <div className="map-container" dir={dir}>
        <div className="cancel" onClick={props?.onClose}>
          <CancelIcon className="icon" />
        </div>
        <p>
          {isEditMode
            ? t("editAddress") || "تعديل العنوان"
            : t("addNewAddress") || "اضافة عنوان جديد"}
        </p>

        {/* Map Component */}
        <div className="map-wrapper">
          {position ? (
            <MapContainer
              center={position}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
              whenReady={(map) => {
                mapRef.current = map.target;
                setTimeout(() => {
                  map.target.invalidateSize();
                }, 100);
              }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <MarkerComponent
                position={position}
                dragEnd={handleMarkerDragEnd}
                draggable={true}
              />
              <MapEvents onMapClick={handleMapClick} />
            </MapContainer>
          ) : (
            <div>Loading map...</div>
          )}

          {/* Current location button */}
          <div
            className="current-location-button"
            onClick={handleCurrentLocationClick}
          >
            <LocationIcon />
          </div>

          {/* Map instruction banner */}
          {!formData.locationConfirmed && (
            <div className="map-instruction-banner" style={{ direction: dir }}>
              {dir === "rtl"
                ? "يرجى النقر على الخريطة أو استخدام زر الموقع الحالي لتأكيد موقعك"
                : "Please click on the map or use current location button to confirm your location"}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="control-group">
            <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
              <label className="form-label">
                {t("addressName") || "اسم العنوان"}
              </label>
              <TextField
                fullWidth
                className="form-input"
                required
                placeholder={t("placeholder_addressName")}
                name="addressName"
                value={formData.addressName}
                onChange={handleInputChange}
                error={!!formErrors.addressName}
                helperText={formErrors.addressName}
                inputProps={{
                  style: {
                    textAlign: dir === "rtl" ? "right" : "left",
                    direction: dir,
                  },
                }}
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
              <label className="form-label">{t("area") || "المنطقة"}</label>
              <Select
                fullWidth
                className="form-input"
                required
                value={formData.area}
                onChange={handleAreaChange}
                error={!!formErrors.area}
                displayEmpty
                inputProps={{
                  style: {
                    textAlign: dir === "rtl" ? "right" : "left",
                    direction: dir,
                  },
                }}
                sx={{
                  "& .MuiSelect-nativeInput": {
                    display: "none !important",
                  },
                  textAlign: "center",
                }}
              >
                <MenuItem value="" disabled>
                  {t("selectArea") || "اختر المنطقة"}
                </MenuItem>
                {SaudiArabiaAreas.map((area) => (
                  <MenuItem key={area.areaId} value={area.areaId}>
                    {language === "ar"
                      ? area.areaNameArabic
                      : area.areaNameEnglish}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.area && (
                <span
                  style={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    marginTop: "3px",
                  }}
                >
                  {formErrors.area}
                </span>
              )}
            </FormControl>

            <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
              <label className="form-label">
                {t("governorate") || "المحافظة"}
              </label>
              <Select
                fullWidth
                className="form-input"
                required
                value={formData.governorate}
                onChange={handleGovernorateChange}
                error={!!formErrors.governorate}
                disabled={!formData.area}
                displayEmpty
                inputProps={{
                  style: {
                    textAlign: dir === "rtl" ? "right" : "left",
                    direction: dir,
                  },
                }}
                sx={{
                  "& .MuiSelect-nativeInput": {
                    display: "none !important",
                  },
                  textAlign: "center",
                }}
              >
                <MenuItem value="" disabled>
                  {t("selectGovernorate") || "اختر المحافظة"}
                </MenuItem>
                {availableGovernorates.map((gov) => (
                  <MenuItem key={gov.id} value={gov.id}>
                    {language === "ar" ? gov.nameArabic : gov.nameEnglish}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.governorate && (
                <span
                  style={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    marginTop: "3px",
                  }}
                >
                  {formErrors.governorate}
                </span>
              )}
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
              <label className="form-label">{t("street") || "الشارع"}</label>
              <TextField
                fullWidth
                className="form-input"
                required
                placeholder={t("placeholder_street")}
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                error={!!formErrors.street}
                helperText={formErrors.street}
                inputProps={{
                  style: {
                    textAlign: dir === "rtl" ? "right" : "left",
                    direction: dir,
                  },
                }}
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
              <label className="form-label">
                {t("buildingNumber") || "رقم المبني"}
              </label>
              <TextField
                fullWidth
                className="form-input"
                placeholder={t("placeholder_buildingNumber")}
                name="buildingNumber"
                value={formData.buildingNumber}
                onChange={handleInputChange}
                error={!!formErrors.buildingNumber}
                helperText={formErrors.buildingNumber}
                inputProps={{
                  style: {
                    textAlign: dir === "rtl" ? "right" : "left",
                    direction: dir,
                  },
                }}
              />
            </FormControl>

            <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
              <label className="form-label">
                {t("floorNumber") || "رقم الدور"}
              </label>
              <TextField
                fullWidth
                className="form-input"
                placeholder={t("placeholder_floorNumber")}
                name="floorNumber"
                value={formData.floorNumber}
                onChange={handleInputChange}
                error={!!formErrors.floorNumber}
                helperText={formErrors.floorNumber}
                inputProps={{
                  style: {
                    textAlign: dir === "rtl" ? "right" : "left",
                    direction: dir,
                  },
                }}
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
              <label className="form-label">
                {t("addressSign") || "علامة مميزة"}
              </label>
              <TextField
                fullWidth
                className="form-input"
                placeholder={t("placeholder_addressSign")}
                name="addressSign"
                value={formData.addressSign}
                onChange={handleInputChange}
                inputProps={{
                  style: {
                    textAlign: dir === "rtl" ? "right" : "left",
                    direction: dir,
                  },
                }}
              />
            </FormControl>
          </div>

          <GreenButton
            text={
              isEditMode ? t("update") || "تحديث" : t("confirm") || "تاكيد"
            }
            size="full"
            variant="fill"
            type="submit"
            loading={loading}
          />
        </form>
      </div>
    </Modal>
  );
};

export default Map;